/**
 * TAP API Service Layer
 *
 * This module provides all API communication functions for the TAP application.
 * All functions use Axios with automatic token authentication and handle 401 errors
 * by redirecting to the login page.
 *
 * API Endpoints by Domain:
 *
 * Authentication & Users:
 * - users, createUser, updateUser, deleteUser, userRoles
 *
 * Licensing Jobs:
 * - licensingJobs, licensingJobById, licensingJobHistory
 * - createLicensingJob, updateLicensingJob, copyLicensingJob
 * - runLicensingJob, inactivateLicensingJob, uploadLicensingJob
 *
 * Offer Table:
 * - licensingJobOfferTable, updateOfferTable, pushOfferTable, deleteOfferTable
 * - pushOfferTableBulk, deleteOfferTableBulk, offerTableHistory, uploadOffers
 *
 * Rate Cards:
 * - rateCards, rateCardById, rateCardHistory
 * - createRateCard, saveRateCard, copyRateCard, inactivateRateCard
 * - saveAsTPR, uploadRateCard
 *
 * Window Sequences:
 * - windowSequences, windowSequenceById, windowSequenceHistory
 * - createWindowSequence, updateWindowSequence, copyWindowSequence
 * - inactivateWindowSequence
 *
 * PMX:
 * - pmxDeliveries, pmxPricesAndDates, pmxCatalogUpdates
 * - territories, productPlatforms
 *
 * Jobs & Tasks:
 * - tapJobs, job, jobTasks, jobViewTasks, jobStatuses, jobTypes
 * - updateEngine, startUpdateEngine
 *
 * TPR:
 * - tprExceptions, dismissTprException
 *
 * Reference Data:
 * - format, currency, transactionalWindows, mediaAndTerritories
 * - searchCatalogs, searchDeals
 *
 * Feature Flags:
 * - fetchFeatureFlags
 *
 * @module api
 */
import axios from "axios";
import { getAuthToken } from "./utils/localStorageService";
import { logout } from "./store/auth";
import { API_BASE } from "./config/constants";
import { normalizeUploadErrorResponse } from "./utils/bulkIngestResult.js";

/**
 * Creates an Axios instance for making API requests
 *
 * @param {string} token - The authentication token to be used in the request headers
 * @return {AxiosInstance} - The Axios instance configured with the base URL and headers
 */
export const apiClientV1 = (token) => {
  const instance = axios.create({
    baseURL: API_BASE,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // Add a request interceptor to set Content-Type only for non-FormData
  instance.interceptors.request.use((config) => {
    if (config.data && !(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }
    // Let browser/Axios set Content-Type for FormData (file upload)
    return config;
  });
  return instance;
};

/**
 * Fetches recently changed PMX catalogs for the Home dashboard.
 *
 * Backend performance recommendations (TAP API):
 * - Return a bounded list (e.g. top 10–20 by modified_at DESC)
 * - Project only: id, catalog_id, catalog_name, modified_at
 * - Index modified_at for the recently-changed query
 * - Optional short-lived server cache (30–60s per user)
 *
 * @returns {Promise<Array|Object>} Catalog rows or error payload
 */
async function pmxCatalogUpdates() {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/pmxCuratedDatesAndPrices/recentlyChanged`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching recently updated PMX Catalogs:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function licensingJobStatusForUser() {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/licensingJobStatusForUser`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching licensing job status:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function startUpdateEngine() {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/jobs/update-engine/start`,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function updateEngine(filter, range, sort) {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[${range}]`,
      sort: `[${sort}]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/jobs/update-engine?${params}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching jobs:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function tapJobs(filter, range, sort) {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[${range}]`,
      sort: `[${sort}]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/jobs/tap-jobs?${params}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching jobs:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function licensingJobs(filter, range, sort) {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[${range}]`,
      sort: `[${sort}]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/list?${params}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching jobs:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function job(jobId, isLicensingJob) {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/jobs/${jobId}?isLicensingJob=${isLicensingJob}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching job:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function jobViewTasks(jobId, isLicencingJob, page, limit, taskStatus) {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/jobs/tasks/${jobId}?isLicensingJob=${isLicencingJob}&page=${page}&limit=${limit}&taskStatus=${taskStatus}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching job tasks:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data;
  }
}

async function jobTasks(filter, range, sort) {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[${range}]`,
      sort: `[${sort}]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/jobs/tasks?${params}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching job tasks:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data;
  }
}

async function jobStatuses() {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(`/jobs/jobStatuses`);
    return data;
  } catch (error) {
    console.error("Error fetching job statuses:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function licensingJobStatuses() {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify({}),
      range: `[0,24]`,
      sort: `["id","DESC"]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/jobStatuses?${params}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching job statuses:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function mediaAndTerritories(req) {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify({}),
      sort: `["id","DESC"]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/getMediaTerritoryList/${req}?${params}`,
    );

    return (Array.isArray(data) ? data : []).map((item) => ({
      label: item.label,
      value: item.id,
      data: item,
    }));
  } catch (error) {
    console.error("Error fetching job statuses:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function jobTypes() {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(`/jobs/jobTypes`);
    return data;
  } catch (error) {
    console.error("Error fetching job types:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function pmxPricesAndDates(filter, range, sort) {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[${range}]`,
      sort: `[${sort}]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/pmxCuratedDatesAndPrices?${params}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching pmx prices and dates:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function tprExceptions(filter, range, sort) {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[${range}]`,
      sort: `[${sort}]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/tprExceptions?${params}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching tprExceptions:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function dismissTprException(exceptionId, dismissState) {
  try {
    const { data } = await apiClientV1(getAuthToken()).put(
      `/tprExceptions/${exceptionId}/dismiss/${dismissState}`,
    );
    return data;
  } catch (error) {
    console.error("Error dismiss tprException:", error);
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function territories() {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/pmxCuratedDatesAndPrices/distinctTerritories`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching territories:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data;
  }
}

async function productPlatforms() {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/pmxCuratedDatesAndPrices/distinctProductPlatforms`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching product platforms:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data;
  }
}

async function pmxDeliveries(filter, range, sort) {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[${range}]`,
      sort: `[${sort}]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/pmx/release/deliveries?${params}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching pmx deliveries:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function users(filter, range, sort, { cacheBust = false } = {}) {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[${range}]`,
      sort: `[${sort}]`,
    });

    if (cacheBust) {
      params.set("_", String(Date.now()));
    }

    const { data } = await apiClientV1(getAuthToken()).get(`/users?${params}`, {
      headers: cacheBust
        ? { "Cache-Control": "no-cache", Pragma: "no-cache" }
        : undefined,
    });
    return data;
  } catch (error) {
    console.error("Error fetching users:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function userRoles() {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify({}),
      range: JSON.stringify([0, 99999]),
      sort: `[id, DESC]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/spaUserRoles?${params}`,
    );

    return (Array.isArray(data) ? data : data.items || [])
      .map((item) => ({
        value: item.id,
        label: item.roleName,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  } catch (error) {
    console.error("Error fetching user roles:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function createUser(payload) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(`/users`, payload);
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function updateUser(payload) {
  try {
    const { data } = await apiClientV1(getAuthToken()).put(
      `/users/${payload?.id}`,
      payload,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function deleteUser(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).delete(`/users/${id}`);
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function rateCards(filter, range, sort) {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[${range}]`,
      sort: `[${sort}]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/rateCard?${params}`,
    );
    return data;
  } catch (error) {
    console.error("Error fetching rate cards:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

/**
 * Fetches a job status by its ID from the API
 *
 * @param {string} jobId - The ID of the job to fetch the status for
 * @return {Promise<Object|undefined>} - A promise that resolves with the job status or undefined if an error occurs
 */
async function jobs(jobId) {
  try {
    return await apiClientV1(getAuthToken()).get(`/jobs/${jobId}`);
  } catch (error) {
    console.error(`Error fetching job status for job ID ${jobId}:`, error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data;
  }
}

async function uploadRateCard(formData) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/rateCard/fileUpload`,
      formData,
    );

    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data;
  }
}

async function uploadLicensingJob(formData) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/licensing/fileUpload`,
      formData,
      { timeout: 0 },
    );

    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return normalizeUploadErrorResponse(error);
  }
}

async function licensingBulkIngestResult(jobTrackerId) {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/bulkIngestResult/${jobTrackerId}`,
    );

    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data;
  }
}

/** Copies a licensing job by ID */
async function copyLicensingJob(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/licensing/copy`,
      id,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

/** Bulk copies multiple licensing jobs */
async function copyLicensingJobBulk(ids) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/licensing/copyBulk`,
      ids,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function runLicensingJob(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/licensing/runJob`,
      id,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function runLicensingJobBulk(ids) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/licensing/runBulk`,
      ids,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function checkForUpdates(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/licensing/checkForUpdates`,
      id,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function inactivateLicensingJob(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).put(
      `/licensing/inactivate`,
      id,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function inactivateLicensingJobBulk(ids) {
  try {
    const { data } = await apiClientV1(getAuthToken()).put(
      `/licensing/inactivateBulk`,
      ids,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function exportLicensingJob(ids) {
  try {
    const filter = { id: ids };
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/list?${params}`,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function rateCardJobTasks(jobId) {
  try {
    return await apiClientV1(getAuthToken()).get(
      `/jobs/tasks/${jobId}?taskStatus=INVALID&limit=50`,
    );
  } catch (error) {
    console.error(`Error fetching job tasks for job ID ${jobId}:`, error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data;
  }
}

async function copyRateCard(ids) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/rateCard/copyBulk`,
      ids,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function saveAsTPR(ids) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/rateCard/saveAsTprBulk`,
      ids,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function inactivateRateCard(ids) {
  try {
    const { data } = await apiClientV1(getAuthToken()).delete(
      `/rateCard/deleteBulk`,
      { data: ids },
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function exportRateCard(ids) {
  try {
    const filter = { id: ids };
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/rateCard?${params}`,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function rateCardById(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(`/rateCard/${id}`);
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function rateCardHistory(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/rateCard/${id}/history`,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

/** Fetches available format options */
async function format() {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(`/lists/format`);

    return data.map((item) => ({
      label: `${item.label}`,
      value: item.label,
      info: item.label,
      listId: item.id,
    }));
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return [];
    }
    console.error("Error fetching format options:", error);
    return [];
  }
}

/** Fetches available currency options */
async function currency() {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(`/lists/currency`);

    return data.map((item) => ({
      label: `${item.label} - ${item.description}`,
      value: item.id,
      info: item.label,
    }));
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return [];
    }
    console.error("Error fetching currency options:", error);
    return [];
  }
}

async function windowSequence() {
  try {
    const filter = { status: "ACTIVE" };
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: JSON.stringify([0, 99999]),
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/windowSequence?${params}`,
    );

    const items = Array.isArray(data) ? data : data?.content || [];

    return items.map((item) => ({
      label: item.searchableTitle,
      value: item.id,
      data: item,
    }));
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function windowSequences(filter, range, sort) {
  try {
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[${range}]`,
      sort: `[${sort}]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/windowSequence?${params}`,
    );

    return data;
  } catch (error) {
    console.error("Error fetching window sequences:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function copyWindowSequence(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).put(
      `/windowSequence/copy/${id}`,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    throw error;
  }
}

async function copyWindowSequenceBulk(ids) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/windowSequence/copyBulk`,
      ids,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function inactivateWindowSequence(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).delete(
      `/windowSequence/${id}`,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    throw error;
  }
}

async function inactivateWindowSequenceBulk(ids) {
  try {
    const { data } = await apiClientV1(getAuthToken()).delete(
      `/windowSequence/deleteBulk`,
      { data: ids },
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function exportWindowSequence(ids) {
  try {
    const filter = { id: ids };
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/windowSequence?${params}`,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function createWindowSequence(windowSequenceData) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/windowSequence`,
      windowSequenceData,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function updateWindowSequence(id, windowSequenceData) {
  try {
    const { data } = await apiClientV1(getAuthToken()).put(
      `/windowSequence/${id}`,
      windowSequenceData,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function windowSequenceById(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/windowSequence/${id}`,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function windowSequenceHistory(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/windowSequence/${id}/history`,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    console.error(`Error fetching window sequence history for ${id}:`, error);
    return [];
  }
}

async function createRateCard(rateCard) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/rateCard`,
      rateCard,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    const errorData = error?.response?.data;
    const message =
      errorData?.error?.message ||
      errorData?.message ||
      "Error creating Rate Card.";
    throw new Error(message);
  }
}

async function saveRateCard(rateCard) {
  try {
    const { data } = await apiClientV1(getAuthToken()).put(
      `/rateCard/${rateCard.id}`,
      rateCard,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    const errorData = error?.response?.data;
    let message = errorData?.error?.message || errorData?.message;
    if (!message && errorData?.errors && typeof errorData.errors === "object") {
      const parts = Object.values(errorData.errors).filter(Boolean);
      if (parts.length) {
        message = parts.join(" ");
      }
    }
    const status = error?.response?.status;
    throw new Error(
      message ||
        (status
          ? `Error saving Rate Card (HTTP ${status}).`
          : "Error saving Rate Card."),
    );
  }
}

async function licensingJobById(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/list/${id}`,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function licensingJobHistory(id) {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/${id}/history`,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }
    return error?.response?.data || { error: "No response body" };
  }
}

async function searchCatalogs(searchTerm) {
  try {
    const filter = searchTerm ? { q: searchTerm } : {};
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[0,99]`,
      sort: `["id","DESC"]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/searchCatalogItemsAPI?${params}`,
    );

    return data;
  } catch (error) {
    console.error("Error searching catalogs:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function searchDeals(searchTerm) {
  try {
    const filter = searchTerm ? { q: searchTerm } : {};
    const params = new URLSearchParams({
      filter: JSON.stringify(filter),
      range: `[0,99]`,
      sort: `["id","DESC"]`,
    });

    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/searchDealsAPI?${params}`,
    );

    return data;
  } catch (error) {
    console.error("Error searching deals:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function createLicensingJob(payload) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/licensing`,
      payload,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

async function updateLicensingJob(id, payload) {
  try {
    const { data } = await apiClientV1(getAuthToken()).put(
      `/licensing/${id}`,
      payload,
    );
    return data;
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

/**
 * Fetches offer table data for a specific licensing job
 * @param {number} jobId - The licensing job ID
 * @returns {Promise<Array<OfferTableDTO>>} - List of offer table records
 */
async function licensingJobOfferTable(jobId) {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/${jobId}/offerTable`,
    );
    return data;
  } catch (error) {
    console.error(`Error fetching offer table for job ${jobId}:`, error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

/**
 * Updates an offer table record
 * @param {object} offer - The offer table record to update
 * @returns {Promise<OfferTableDTO>} - The updated offer table record
 */
async function updateOfferTable(offer) {
  try {
    const { data } = await apiClientV1(getAuthToken()).put(
      `/licensing/offerTable/${offer.id}`,
      offer,
    );
    return data;
  } catch (error) {
    console.error(`Error updating offer ${offer.id}:`, error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

/**
 * Pushes an offer table record to RightsLine
 * @param {number} offerId - The offer table record ID
 * @returns {Promise<object>} - The result of the push operation
 */
async function pushOfferTable(offerId) {
  try {
    const { data } = await apiClientV1(getAuthToken()).put(
      `/licensing/pushOfferTable/${offerId}`,
    );
    return data;
  } catch (error) {
    console.error(`Error pushing offer ${offerId}:`, error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

/**
 * Deletes an offer table record
 * @param {number} offerId - The offer table record ID
 * @returns {Promise<object>} - The result of the delete operation
 */
async function deleteOfferTable(offerId) {
  try {
    const { data } = await apiClientV1(getAuthToken()).delete(
      `/licensing/deleteOfferTable/${offerId}`,
    );
    return data;
  } catch (error) {
    console.error(`Error deleting offer ${offerId}:`, error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

/**
 * Bulk push offer table records to RightsLine
 * @param {Array<number>} offerIds - Array of offer table record IDs
 * @returns {Promise<object>} - The result of the bulk push operation
 */
async function pushOfferTableBulk(offerIds) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/licensing/pushUpdateOfferBulk`,
      offerIds,
    );
    return data;
  } catch (error) {
    console.error(`Error bulk pushing offers:`, error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

/**
 * Bulk delete offer table records
 * @param {Array<number>} offerIds - Array of offer table record IDs
 * @returns {Promise<object>} - The result of the bulk delete operation
 */
async function deleteOfferTableBulk(offerIds) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/licensing/deleteOfferBulk`,
      offerIds,
    );
    return data;
  } catch (error) {
    console.error(`Error bulk deleting offers:`, error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

/**
 * Fetches offer history for a specific offer
 * @param {number} offerId - The offer table record ID
 * @returns {Promise<Array>} - List of offer history records
 */
async function offerTableHistory(offerId) {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/licensing/${offerId}/offerTableHistory`,
    );
    return data;
  } catch (error) {
    console.error(`Error fetching offer history for ${offerId}:`, error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

/**
 * Upload offers from file
 * @param {FormData} formData - Form data containing the file
 * @returns {Promise<object>} - The job tracker ID
 */
async function uploadOffers(formData) {
  try {
    const { data } = await apiClientV1(getAuthToken()).post(
      `/licensing/offer/fileUpload`,
      formData,
    );
    return data;
  } catch (error) {
    console.error("Error uploading offers:", error);

    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return;
    }

    return error?.response?.data || { error: "No response body" };
  }
}

/** Fetches available transactional window options */
async function transactionalWindows() {
  try {
    const { data } = await apiClientV1(getAuthToken()).get(
      `/lists/transactionalWindows`,
    );

    return data.map((item) => ({
      label: item.label,
      value: item.id,
      description: item.description,
    }));
  } catch (error) {
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return [];
    }
    console.error("Error fetching transactional window options:", error);
    return [];
  }
}

/**
 * Fetches enabled feature flags from the API
 * @returns {Promise<string[]>} Array of enabled feature flag names
 */
async function fetchFeatureFlags() {
  try {
    const { data } = await apiClientV1(getAuthToken()).get("/feature");

    if (Array.isArray(data)) {
      // Filter to only enabled flags and extract the flag names
      const enabledFlags = data
        .filter((item) => item.featureEnabled)
        .map((item) => item.controlledFeature);
      return enabledFlags;
    }
    return [];
  } catch (error) {
    console.error("Error fetching feature flags:", error);
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return [];
    }
    return [];
  }
}

/**
 * Extends the expiration of an existing JWT token
 * @param {string} token - The current valid JWT token
 * @returns {Promise<string|null>} The new extended token, or null if refresh failed
 */
async function extendToken(token) {
  try {
    const response = await axios.post(`${API_BASE}/users/extendToken`, null, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    // Backend returns token as JSON string with quotes
    let newToken = response.data;
    if (typeof newToken === "string") {
      // Remove surrounding quotes if present
      newToken = newToken.replace(/^"|"$/g, "");
    }
    return newToken;
  } catch (error) {
    console.error("Error extending token:", error);
    if (error?.response?.status === 401) {
      handleUnauthorizedRequest();
      return null;
    }
    return null;
  }
}

/**
 * Handles unauthorized requests by logging out the user and redirecting to the login page
 */
const handleUnauthorizedRequest = () => {
  const currentPath = window.location.pathname + window.location.search;
  let loginPath = "/login";
  if (currentPath && currentPath !== "/login") {
    loginPath = `/login?postLoginRedirect=${encodeURIComponent(currentPath)}`;
  }
  logout();
  window.location.assign(loginPath);
};

export const tapApi = {
  pmxCatalogUpdates,
  licensingJobStatusForUser,
  startUpdateEngine,
  updateEngine,
  job,
  tapJobs,
  jobViewTasks,
  jobTasks,
  jobTypes,
  jobStatuses,
  pmxDeliveries,
  pmxPricesAndDates,
  territories,
  productPlatforms,
  users,
  userRoles,
  createUser,
  updateUser,
  deleteUser,
  tprExceptions,
  dismissTprException,
  rateCards,
  jobs,
  uploadRateCard,
  rateCardJobTasks,
  copyRateCard,
  saveAsTPR,
  inactivateRateCard,
  exportRateCard,
  rateCardById,
  rateCardHistory,
  format,
  currency,
  windowSequence,
  windowSequences,
  windowSequenceById,
  windowSequenceHistory,
  copyWindowSequence,
  copyWindowSequenceBulk,
  inactivateWindowSequence,
  inactivateWindowSequenceBulk,
  exportWindowSequence,
  createWindowSequence,
  updateWindowSequence,
  createRateCard,
  saveRateCard,
  licensingJobs,
  licensingJobStatuses,
  mediaAndTerritories,
  uploadLicensingJob,
  licensingBulkIngestResult,
  copyLicensingJob,
  copyLicensingJobBulk,
  runLicensingJob,
  runLicensingJobBulk,
  checkForUpdates,
  inactivateLicensingJob,
  inactivateLicensingJobBulk,
  exportLicensingJob,
  licensingJobById,
  licensingJobHistory,
  searchCatalogs,
  searchDeals,
  createLicensingJob,
  updateLicensingJob,
  transactionalWindows,
  licensingJobOfferTable,
  updateOfferTable,
  pushOfferTable,
  deleteOfferTable,
  pushOfferTableBulk,
  deleteOfferTableBulk,
  offerTableHistory,
  uploadOffers,
  fetchFeatureFlags,
  extendToken,
};
